import type { AnchorHTMLAttributes } from 'react';
import { isLinkItem } from './link-item';
import type { LinkItem } from './link-item';
import { isSafeHref } from './is-safe-href';

/**
 * The props an `<a>` receives, picked from React's own anchor attributes so
 * the object can be spread onto an element without a cast at the call site.
 * `href` is required: an anchor without one has no link role, is not
 * focusable, and is invisible to assistive technology while still looking
 * like a link.
 */
export type AnchorAttributes = Pick<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'target' | 'rel' | 'title' | 'aria-label' | 'download' | 'hrefLang' | 'referrerPolicy'
> & { href: string };

const BLANK_TARGET = '_blank';

/** `target="_blank"` opens a window that can reach back into this one without these. */
const IMPLIED_REL_FOR_BLANK_TARGET = ['noopener', 'noreferrer'];

const cleanString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
};

/**
 * The author's tokens and their order are kept; only the missing implied ones
 * are appended. Deduping is case-insensitive because `rel` tokens are.
 */
const resolveRel = (authorRel: string | undefined, target: string | undefined): string | undefined => {
  const tokens = authorRel ? authorRel.split(/\s+/) : [];

  if (target?.toLowerCase() !== BLANK_TARGET) {
    return tokens.length > 0 ? tokens.join(' ') : undefined;
  }

  const present = new Set(tokens.map((token) => token.toLowerCase()));
  const merged = [...tokens];
  IMPLIED_REL_FOR_BLANK_TARGET.forEach((token) => {
    if (!present.has(token)) {
      merged.push(token);
    }
  });
  return merged.join(' ');
};

const resolveDownload = (download: LinkItem['download']): string | true | undefined => {
  if (download === true) {
    return true;
  }
  return cleanString(download);
};

/**
 * Turns a stored link entry into anchor props, or `undefined` when the entry
 * cannot produce a usable anchor — missing, cleared, blank `href`, or an
 * `href` the safety check rejects. Callers render the plain text in that case.
 *
 * Knows nothing about text, markers, or any one component: this is the piece
 * every linking component shares.
 */
export function resolveLinkAttributes(item: LinkItem | undefined): AnchorAttributes | undefined {
  if (!isLinkItem(item)) {
    return undefined;
  }

  const href = cleanString(item.href);
  if (!isSafeHref(href)) {
    return undefined;
  }

  const target = cleanString(item.target);
  const rel = resolveRel(cleanString(item.rel), target);
  const title = cleanString(item.title);
  const ariaLabel = cleanString(item.ariaLabel);
  const download = resolveDownload(item.download);
  const hrefLang = cleanString(item.hreflang);
  const referrerPolicy = cleanString(item.referrerPolicy);

  return {
    href: href as string,
    ...(target !== undefined ? { target } : {}),
    ...(rel !== undefined ? { rel } : {}),
    ...(title !== undefined ? { title } : {}),
    ...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {}),
    ...(download !== undefined ? { download } : {}),
    ...(hrefLang !== undefined ? { hrefLang } : {}),
    // Stored values are untyped strings; React narrows this prop to a union.
    ...(referrerPolicy !== undefined
      ? { referrerPolicy: referrerPolicy as AnchorAttributes['referrerPolicy'] }
      : {}),
  };
}
